import type { BillingView, TenantView } from "@mailhand/core/api";
import type { Action } from "@mailhand/core/schemas";
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { errorMessage } from "../lib/useResource.js";
import { ErrorNote, PageSpinner } from "../ui/index.js";
import { useApi } from "./services.js";

interface TenantData {
  view: TenantView;
  actions: Action[];
  pendingApprovals: number;
  billing: BillingView;
}

interface TenantContextValue extends TenantData {
  setView: (view: TenantView) => void;
  setActions: (actions: Action[]) => void;
  reloadView: () => Promise<void>;
  reloadActions: () => Promise<void>;
  reloadApprovals: () => Promise<void>;
  setBilling: (billing: BillingView) => void;
  reloadBilling: () => Promise<BillingView>;
}

const TenantContext = createContext<TenantContextValue | null>(null);

type LoadState = { status: "loading" } | { status: "error"; message: string } | { status: "ready"; data: TenantData };

/** The active tenant, its actions, the approval count, and its billing: what every screen and the header read. */
export function TenantProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  const loadAll = useCallback(async () => {
    try {
      const [view, actionList, approvals, billing] = await Promise.all([
        api.call("tenantGet", {}),
        api.call("actionsList", {}),
        api.call("approvalsList", {}),
        api.call("billingGet", {}),
      ]);
      setState({ status: "ready", data: { view, actions: actionList.actions, pendingApprovals: approvals.approvals.length, billing } });
    } catch (error) {
      setState({ status: "error", message: errorMessage(error) });
    }
  }, [api]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const patch = useCallback((change: Partial<TenantData>) => {
    setState((current) => (current.status === "ready" ? { status: "ready", data: { ...current.data, ...change } } : current));
  }, []);

  const value = useMemo<TenantContextValue | null>(() => {
    if (state.status !== "ready") return null;
    return {
      ...state.data,
      setView: (view) => { patch({ view }); },
      setActions: (actions) => { patch({ actions }); },
      reloadView: async () => { patch({ view: await api.call("tenantGet", {}) }); },
      reloadActions: async () => { patch({ actions: (await api.call("actionsList", {})).actions }); },
      reloadApprovals: async () => { patch({ pendingApprovals: (await api.call("approvalsList", {})).approvals.length }); },
      setBilling: (billing) => { patch({ billing }); },
      reloadBilling: async () => {
        const billing = await api.call("billingGet", {});
        patch({ billing });
        return billing;
      },
    };
  }, [state, patch, api]);

  if (state.status === "loading") return <PageSpinner />;
  if (state.status === "error") {
    return (
      <div className="page">
        <ErrorNote message={state.message} onRetry={() => void loadAll()} />
      </div>
    );
  }
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);
  if (value === null) throw new Error("TenantProvider is missing");
  return value;
}

/** Setup is done when the tenant has an inbox, instructions, and one enabled forward or webhook action. */
export function isSetupComplete(view: TenantView, actions: readonly Action[]): boolean {
  const hasHandoff = actions.some((action) => action.enabled && (action.type === "forward" || action.type === "webhook"));
  return view.inboxes.length > 0 && view.tenant.instructions.trim() !== "" && hasHandoff;
}
